import React, { Dispatch, ReactElement, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Table } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { SearchOutlined } from '@ant-design/icons';
import {
  FilterDropdownProps,
  Key,
  SorterResult,
  TableCurrentDataSource,
  TablePaginationConfig,
} from 'antd/lib/table/interface';

import { PackageExpandedRow } from 'leash/components/packageExpandedRow/PackageExpandedRow';
import { TableEmpty } from 'leash/components/packagesTable/TableEmpty';
import { PackagesTableStyles } from 'leash/components/packagesTable/PackagesTable.styles';
import PackagesTableFooterTools from 'leash/components/packagesTable/PackagesTableFooterTools';
import { PackagesAction, PackagesState } from 'leash/reducers/packages/PackagesReducer';
import {
  filterTableMoment,
  onClickOpenDetailsView,
} from 'leash/utilities/packages/packages.utilities';
import { testId } from 'leash/utilities/test/dataTestId';
import { PackagesTableActiveFooterTools } from 'leash/types';
import { TableFilterDropdownStyled } from 'shared/components/table/TableFilterDropdown.styles';
import colors from 'theme/colors';
import date from 'utilities/dates/date';
import { filterTableValue } from 'utilities/table/table';
import { dateSort, defaultSort } from 'utilities/sorts/sorts';
import { FacetList, TravelPlan, TableFilter } from 'types';

function getColumns(
  dispatch: Dispatch<PackagesAction>,
  filters: TableFilter,
  facets: FacetList,
) {
  return useMemo<ColumnType<TravelPlan>[]>(
    () => [
      {
        title: 'Contact ID',
        dataIndex: 'leadId',
        sorter: (a, b) => defaultSort(a?.leadId ?? '', b?.leadId ?? ''),
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: 'Package',
        className: 'package-column',
        dataIndex: ['travelPackage', 'travelType'],
        filters: facets?.travelTypes,
        filteredValue: filters?.['travelPackage.travelType'] ?? null,
        onFilter: (value, record: TravelPlan) =>
          filterTableValue(value, String(record.travelPackage.travelTypeId)),
      },
      {
        title: 'Method',
        className: 'method-column',
        dataIndex: ['travelPackage', 'travelMethod'],
        filters: facets?.travelMethods,
        filteredValue: filters?.['travelPackage.travelMethod'] ?? null,
        onFilter: (value, record: TravelPlan) =>
          filterTableValue(value, String(record.travelPackage.travelMethodId)),
      },
      {
        title: 'Prop. Status',
        className: 'column-status',
        dataIndex: ['travelPackage', 'workflowStatus'],
        filters: facets?.workflowStatus,
        filteredValue: filters?.['travelPackage.workflowStatus'] ?? null,
        onFilter: (value, record: TravelPlan) =>
          filterTableValue(value, record.travelPackage.workflowStatus),
      },
      {
        title: 'Breed. State',
        dataIndex: ['breeder', 'stateAbbr'],
        filters: facets?.states,
        filteredValue: filters?.['breeder.stateAbbr'] ?? null,
        onFilter: (value, record: TravelPlan) =>
          filterTableValue(value, record.breeder.stateAbbr),
      },
      {
        title: 'Cust. State',
        key: 'customer.stateAbbr',
        filters: facets?.states,
        filteredValue: filters?.['customer.stateAbbr'] ?? null,
        onFilter: (value, record: TravelPlan) =>
          filterTableValue(value, record.address.state || record.customer.stateAbbr),
        render: ({ address, customer }: TravelPlan) =>
          address.state || customer.stateAbbr,
      },
      {
        title: 'Breeder',
        dataIndex: ['breeder', 'name'],
        filters: facets?.breeder,
        filteredValue: filters?.['breeder.name'] ?? null,
        onFilter: (value, record: TravelPlan) =>
          filterTableValue(value, record.breeder.name),
      },
      {
        title: 'Breeder Tier',
        dataIndex: ['breeder', 'breederTier'],
        filters: facets?.breederTier,
        filteredValue: filters?.['breeder.breederTier'] ?? null,
        onFilter: (value, record: TravelPlan) =>
          filterTableValue(value, record.breeder.breederTier),
      },
      {
        title: 'Puppy ID',
        dataIndex: ['puppy', 'id'],
        filters: facets?.puppy,
        filteredValue: filters?.['puppy.id'] ?? null,
        onFilter: (value, record: TravelPlan) =>
          filterTableValue(value, String(record.puppy.id)),
        sorter: (a, b) => defaultSort(a?.puppy?.id ?? '', b?.puppy?.id ?? ''),
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: 'Breed',
        dataIndex: ['puppy', 'breed'],
        filteredValue: filters?.['puppy.breed'] ?? null,
        onFilter: (value, record: TravelPlan) =>
          filterTableValue(value, record.puppy.breed),
        filterIcon: (filtered) => (
          <SearchOutlined style={{ color: filtered ? colors.primary : undefined }} />
        ),
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          prefixCls,
          visible,
        }: FilterDropdownProps) => (
          <TableFilterDropdownStyled
            dataIndex="puppy.breed"
            onSearch={(value: React.Key[], dataIndex: string) => {
              dispatch({
                type: 'FILTER_PACKAGES',
                payload: { ...filters, [dataIndex]: value },
              });
            }}
            prefixCls={prefixCls}
            setSelectedKeys={setSelectedKeys}
            selectedKeys={selectedKeys}
            confirm={confirm}
            clearFilters={clearFilters}
            visible={visible}
          />
        ),
        sorter: (a, b) => defaultSort(a?.puppy?.breed ?? '', b?.puppy?.breed ?? ''),
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: 'Avail.',
        dataIndex: ['puppy', 'dateAvailable'],
        render: (value: string) => <span>{date.toUSLocale(value)}</span>,
        sorter: (a, b) =>
          dateSort(a?.puppy?.dateAvailable, b?.puppy?.dateAvailable ?? ''),
        sortDirections: ['descend', 'ascend'],
      },
      {
        render: () => null,
        className: 'hidden',
        title: 'Departure Date',
        dataIndex: ['travelPackage', 'travelProposal', 'departureTime'],
        filteredValue: filters?.['travelPackage.travelProposal.departureTime'] ?? null,
        onFilter: (value, record: TravelPlan) =>
          filterTableMoment(
            value as string,
            record?.travelPackage?.travelProposal?.departureTime ?? undefined,
            true,
          ),
      },
      {
        render: () => null,
        className: 'hidden',
        title: 'Arrival Date',
        dataIndex: ['travelPackage', 'travelProposal', 'arrivalTime'],
        filteredValue: filters?.['travelPackage.travelProposal.arrivalTime'] ?? null,
        onFilter: (value, record: TravelPlan) =>
          filterTableMoment(
            value as string,
            record?.travelPackage?.travelProposal?.arrivalTime ?? undefined,
            false,
          ),
      },
    ],
    [filters, facets],
  );
}

interface Props {
  packagesState: PackagesState;
  dispatch: Dispatch<PackagesAction>;
  activeFooterTools?: PackagesTableActiveFooterTools;
}

export const PackagesTable = (props: Props): ReactElement => {
  const [pageSize, setPageSize] = useState(25);
  const [currentDataSource, setCurrentDataSource] = useState<TravelPlan[]>([]);
  const {
    packagesState: { loading, filters, packages, facets },
    dispatch,
    activeFooterTools,
  } = props;
  const history = useHistory();

  const handleTableChange = (
    paginationConfig: TablePaginationConfig,
    filters: Record<string, (Key | boolean)[] | null>,
    _: SorterResult<TravelPlan> | SorterResult<TravelPlan>[],
    extra: TableCurrentDataSource<TravelPlan>,
  ): void => {
    setCurrentDataSource(extra.currentDataSource);
    if (paginationConfig.pageSize) {
      setPageSize(paginationConfig.pageSize);
    }
    dispatch({ type: 'FILTER_PACKAGES', payload: filters as TableFilter });
  };

  const columns = getColumns(dispatch, filters, facets);

  useEffect(() => {
    setCurrentDataSource(packages);
  }, [packages]);

  return (
    <PackagesTableStyles>
      <Table<TravelPlan>
        data-testid={testId.packagesTable}
        rowKey={(travelPlan) => travelPlan.leadId}
        onChange={handleTableChange}
        loading={loading}
        locale={{
          emptyText: <TableEmpty loading={loading} />,
        }}
        footer={() => (
          <PackagesTableFooterTools
            currentDataSource={currentDataSource}
            activeFooterTools={activeFooterTools}
          />
        )}
        dataSource={packages}
        columns={columns}
        pagination={{
          position: ['bottomCenter'],
          pageSize,
          showSizeChanger: true,
        }}
        expandable={{
          expandedRowRender: (record: TravelPlan) => (
            <PackageExpandedRow
              key={record.key}
              travelPlan={record}
              dispatch={dispatch}
            />
          ),
        }}
        onRow={(record) => {
          return {
            onClick: (e) => onClickOpenDetailsView(record, e, history),
          };
        }}
      />
    </PackagesTableStyles>
  );
};
